import {
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  CellContext,
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import ReactSelect from "react-select"
import toast from "react-hot-toast"
import styles from 'components/customScenarioBuilderModal/customScenarioBuilderModal.module.scss'
import { CustomScenarioDetails } from "components/createCustomScenarioModal/createCustomScenarioModal"
import { TrafficParamNameResponse } from "services/sicalcApi/responses/trafficParamNameListResponse"
import { TrafficSummaryRowResponse } from "services/sicalcApi/responses/trafficSummaryListResponse"
import { CreateCustomScenarioRequest } from "services/sicalcApi/requests/createCustomScenarioRequest"
import { useSicalcApiService } from "services/sicalcApi/sicalcApiService"
import { getServerErrorMessage } from "services/sicalcApi/errors/getServerErrorMessage"
import { ModalBase } from "components/common/modals/modalBase/modalBase"
import { ModalProps } from "services/modal/modalService"
import { PrimaryButton } from "components/common/buttons/primaryButton/primaryButton"
import { LightButton } from "components/common/buttons/lightButton/lightButton"
import {
  allTrafficFilter,
  ariaSort,
  buildFilterString,
  filterMatchesRow,
  filterSeparators,
  findParamValue,
  formatNumber,
  formatScalingFactor,
  norwegianTextSort,
  sortIndicatorClassName,
} from "components/trafficTable/trafficTableHelpers"

export interface CustomScenarioBuilderModalProps extends ModalProps {
  calculationTaskId: string
  noiseSourceId: string
  details: CustomScenarioDetails
  onCreated?: () => void
}

interface Modifier {
  description: string
  filterString: string
  scalingFactor: number
}

/** One "category = values" line in the filter being built */
interface FilterCriterion {
  paramKey: string
  values: string[]
}

/** A row of the base scenario's traffic with the effect of the filters applied */
interface PreviewRow {
  id: string
  paramValues: { [paramKey: string]: string }
  operations: number
  scale: number
  result: number
  isExcluded: boolean
  isChanged: boolean
}

interface ValueOption {
  label: string
  value: string
}

const numericColumnIds = [ 'operations', 'scale', 'result' ]

const isNumericColumn = (columnId: string) => numericColumnIds.includes(columnId)

const emptyCriterion: FilterCriterion = {
  paramKey: '',
  values: [],
}

export function CustomScenarioBuilderModal(props: CustomScenarioBuilderModalProps) {
  const { customScenarioApi, trafficApi } = useSicalcApiService()

  const [ paramNames, setParamNames ] = useState<TrafficParamNameResponse[]>([])
  const [ paramValues, setParamValues ] = useState<{ [paramKey: string]: string[] }>({})
  const [ rows, setRows ] = useState<TrafficSummaryRowResponse[]>([])
  const [ modifiers, setModifiers ] = useState<Modifier[]>([])
  const [ sorting, setSorting ] = useState<SortingState>([])
  const [ hiddenColumnIds, setHiddenColumnIds ] = useState<Set<string>>(new Set())
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSaving, setIsSaving ] = useState(false)
  const [ errorMessage, setErrorMessage ] = useState<string>()

  // The filter being built: one or more categories, combined with AND
  const [ appliesToAllTraffic, setAppliesToAllTraffic ] = useState(false)
  const [ criteria, setCriteria ] = useState<FilterCriterion[]>([ emptyCriterion ])
  const [ newScalingFactor, setNewScalingFactor ] = useState<string>('1')

  const { startWithNoTraffic, baseScenarioId } = props.details
  const defaultScale = startWithNoTraffic ? 0 : 1

  useEffect(() => {
    let cancelled = false

    trafficApi.getParamNames()
      .then(response => {
        if (!cancelled) {
          setParamNames(response.paramNames)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMessage("Kunne ikke hente hvilke filtre som er tilgjengelige")
        }
      })

    return () => {
      cancelled = true
    }
  }, [ trafficApi ])

  // The traffic of the base scenario, grouped by every parameter, so each row is
  // one distinct combination. This is what the filters are matched against.
  useEffect(() => {
    let cancelled = false

    if (paramNames.length === 0) {
      return
    }

    const paramKeys = paramNames.map(p => p.key)
    setIsLoading(true)

    Promise.all([
      trafficApi.getParamValues({
        noiseSourceId: props.noiseSourceId,
        scenarioId: baseScenarioId,
        paramKeys,
      }),
      trafficApi.getSummary({
        calculationTaskId: props.calculationTaskId,
        scenarioIds: [ baseScenarioId ],
        isCustomScenario: [ false ],
        paramKeys,
      }),
    ])
      .then(([ valuesResponse, summaryResponse ]) => {
        if (!cancelled) {
          setParamValues(valuesResponse.values)
          setRows(summaryResponse.summaryRows)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMessage("Kunne ikke hente trafikken for scenariet")
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [ trafficApi, props.calculationTaskId, props.noiseSourceId, baseScenarioId, paramNames ])

  const displayNameOf = (paramKey: string) =>
    paramNames.find(p => p.key === paramKey)?.displayName ?? paramKey

  const valuesOf = (paramKey: string) => {
    // The values dictionary is keyed by the parameter keys we asked for, but the
    // casing on the wire depends on the server's serialiser settings
    const matchingKey = Object.keys(paramValues)
      .find(key => key.toLowerCase() === paramKey.toLowerCase())

    return matchingKey !== undefined ? paramValues[matchingKey] : []
  }

  /**
   * The rows fed to the table. The scaling is baked into the data rather than
   * read through an accessor, because TanStack caches row values and only
   * rebuilds them when the data itself changes.
   */
  const previewRows = useMemo<PreviewRow[]>(() => rows.map((row, index) => {
    const operations = Object.values(row.scenariosTraffic ?? {})[0]?.operations ?? 0

    // The server multiplies the factor of every matching filter, and when
    // starting from nothing a row that no filter matches is left out entirely
    const matching = modifiers.filter(modifier => filterMatchesRow(modifier.filterString, row))
    const scale = matching.length === 0
      ? defaultScale
      : matching.reduce((product, modifier) => product * modifier.scalingFactor, 1)

    const rowParamValues: { [paramKey: string]: string } = {}
    for (const paramName of paramNames) {
      rowParamValues[paramName.key] = findParamValue(row, paramName.key)
    }

    return {
      id: String(index),
      paramValues: rowParamValues,
      operations,
      scale,
      result: operations * scale,
      isExcluded: scale === 0,
      isChanged: scale !== defaultScale,
    }
  }), [ rows, modifiers, defaultScale, paramNames ])

  const changedRowCount = useMemo(
    () => previewRows.filter(row => row.isChanged).length,
    [ previewRows ])

  const columns = useMemo<ColumnDef<PreviewRow, unknown>[]>(() => [
    ...paramNames.map(param => ({
      id: param.key,
      header: param.displayName,
      accessorFn: (row: PreviewRow) => row.paramValues[param.key] ?? '',
      sortingFn: norwegianTextSort<PreviewRow>(),
    })),
    {
      id: 'operations',
      header: 'Trafikk',
      accessorKey: 'operations',
      cell: (info: CellContext<PreviewRow, unknown>) => formatNumber(info.getValue<number>()),
    },
    {
      id: 'scale',
      header: 'Skalering',
      accessorKey: 'scale',
      cell: (info: CellContext<PreviewRow, unknown>) => formatScalingFactor(info.getValue<number>()),
    },
    {
      id: 'result',
      header: 'Resultat',
      accessorKey: 'result',
      cell: (info: CellContext<PreviewRow, unknown>) => formatNumber(info.getValue<number>()),
    },
  ], [ paramNames ])

  const columnVisibility = useMemo<VisibilityState>(() => {
    const visibility: VisibilityState = {}
    hiddenColumnIds.forEach(columnId => {
      visibility[columnId] = false
    })
    return visibility
  }, [ hiddenColumnIds ])

  const table = useReactTable({
    data: previewRows,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const toggleColumn = (columnId: string) => {
    const nextIds = new Set(hiddenColumnIds)
    if (nextIds.has(columnId)) {
      nextIds.delete(columnId)
    } else {
      nextIds.add(columnId)
    }
    setHiddenColumnIds(nextIds)
  }

  const setCriterion = (index: number, criterion: Partial<FilterCriterion>) => {
    setCriteria(criteria.map((current, i) => i === index
      ? {
        ...current,
        ...criterion,
      }
      : current))
  }

  const addCriterion = () => {
    setCriteria([ ...criteria, emptyCriterion ])
  }

  const removeCriterion = (index: number) => {
    const remaining = criteria.filter((_, i) => i !== index)
    setCriteria(remaining.length > 0 ? remaining : [ emptyCriterion ])
  }

  const resetBuilder = () => {
    setCriteria([ emptyCriterion ])
    setAppliesToAllTraffic(false)
    setNewScalingFactor('1')
  }

  const completeCriteria = criteria.filter(c => c.paramKey !== '' && c.values.length > 0)

  const addModifier = () => {
    const scalingFactor = Number(newScalingFactor.replace(",", "."))
    if (Number.isNaN(scalingFactor) || scalingFactor < 0) {
      toast.error("Skaleringen må være et positivt tall")
      return
    }

    if (!appliesToAllTraffic && completeCriteria.length === 0) {
      toast.error("Velg minst én kategori med verdier")
      return
    }

    const unusableValue = completeCriteria
      .flatMap(c => c.values)
      .find(value => filterSeparators.some(separator => value.includes(separator)))
    if (unusableValue !== undefined) {
      toast.error(`Verdien '${unusableValue}' kan ikke brukes i et filter`)
      return
    }

    // The categories are combined with AND on the server
    const filterString = appliesToAllTraffic
      ? allTrafficFilter
      : buildFilterString(completeCriteria)
    const description = appliesToAllTraffic
      ? "All trafikk"
      : completeCriteria
        .map(c => `${displayNameOf(c.paramKey)} = ${c.values.join(', ')}`)
        .join(' OG ')

    if (modifiers.some(m => m.filterString.toLowerCase() === filterString.toLowerCase())) {
      toast.error("Filteret er allerede lagt til")
      return
    }

    setModifiers([ ...modifiers, {
      description,
      filterString,
      scalingFactor,
    } ])
    resetBuilder()
  }

  const removeModifier = (index: number) => {
    setModifiers(modifiers.filter((_, i) => i !== index))
  }

  const setModifierScale = (index: number, value: string) => {
    const scalingFactor = Number(value.replace(",", "."))
    if (Number.isNaN(scalingFactor)) {
      return
    }
    setModifiers(modifiers.map((modifier, i) => i === index
      ? {
        ...modifier,
        scalingFactor,
      }
      : modifier))
  }

  const create = async () => {
    if (modifiers.length === 0) {
      toast.error("Legg til minst én skalering")
      return
    }

    // Mirrors the server's rule: scaling all traffic only makes sense on its own
    // when the filters adjust existing traffic
    const scalesAllTraffic = modifiers
      .some(m => m.filterString === allTrafficFilter && m.scalingFactor !== 1)
    if (!startWithNoTraffic && scalesAllTraffic && modifiers.length > 1) {
      toast.error("All trafikk kan ikke skaleres sammen med andre filtre")
      return
    }

    const request: CreateCustomScenarioRequest = {
      baseScenarioId,
      name: props.details.name,
      targetYear: props.details.targetYear,
      startWithNoTraffic,
      modifiers: modifiers.map(modifier => ({
        description: modifier.description,
        filterString: modifier.filterString,
        scalingFactor: modifier.scalingFactor,
      })),
    }

    setIsSaving(true)
    try {
      await customScenarioApi.create(props.calculationTaskId, request)
    } catch (error) {
      const message = getServerErrorMessage(error)
      toast.error(message ?? "Kunne ikke opprette scenariet")
      setIsSaving(false)
      return
    }
    setIsSaving(false)

    props.modalContext.close(true)

    if (props.onCreated !== undefined) {
      props.onCreated()
    }
  }

  const dismiss = () => {
    props.modalContext.dismiss()
  }

  const canAddModifier = appliesToAllTraffic || completeCriteria.length > 0

  return (
    <ModalBase
      onDismiss={dismiss}
      heading={`Trafikk i «${props.details.name}»`}
      actions={
        <>
          <LightButton onClick={dismiss}>
            Avbryt
          </LightButton>
          <PrimaryButton onClick={create} submit={true}>
            {isSaving ? "Oppretter ..." : "Opprett"}
          </PrimaryButton>
        </>
      }
    >
      <div className={`${styles.body} row`}>
        <div className={`${styles.panel} column`}>
          <div className={styles.mode}>
            {startWithNoTraffic
              ? "Starter fra null. Trafikken som filtrene treffer blir lagt til."
              : "Starter med all trafikk. Filtrene justerer den; skalering 0 fjerner trafikk."}
          </div>

          <div className={'column'}>
            <div className={'row space-between align-center'}>
              <span className={'form-label'}>Skaleringsliste</span>
              <span className={styles.counter}>
                {`${modifiers.length} skalering${modifiers.length === 1 ? '' : 'er'}`}
              </span>
            </div>
            {modifiers.length === 0 && (
              <div className={styles.empty}>Ingen skaleringer lagt til ennå</div>
            )}
            {modifiers.map((modifier, index) => (
              <div className={`${styles['modifier-row']} row align-center`} key={modifier.filterString}>
                <span className={styles['modifier-label']} title={modifier.filterString}>
                  {modifier.description}
                </span>
                <input
                  className={`input ${styles['scale-input']}`}
                  type="text"
                  value={modifier.scalingFactor}
                  onChange={event => setModifierScale(index, event.target.value)}
                />
                <button
                  className={styles['icon-button']}
                  onClick={() => removeModifier(index)}
                  type="button"
                  aria-label="Fjern skalering"
                ><i className="mdi mdi-delete-outline"></i></button>
              </div>
            ))}
          </div>

          <div className={`${styles.builder} column`}>
            <span className={'form-label'}>Legg til skalering</span>

            <label className={`${styles['all-traffic-toggle']} row align-center`}>
              <input
                type="checkbox"
                checked={appliesToAllTraffic}
                onChange={event => {
                  setAppliesToAllTraffic(event.target.checked)
                  setCriteria([ emptyCriterion ])
                }}
              />
              <span>Gjelder all trafikk</span>
            </label>

            {!appliesToAllTraffic && criteria.map((criterion, index) => {
              const options: ValueOption[] = valuesOf(criterion.paramKey).map(value => ({
                label: value,
                value,
              }))

              return (
                <div className={`${styles['criterion-row']} column`} key={index}>
                  {index > 0 && <span className={styles['and-label']}>OG</span>}
                  <div className={'row align-center'}>
                    <select
                      value={criterion.paramKey}
                      onChange={event => setCriterion(index, {
                        paramKey: event.target.value,
                        values: [],
                      })}
                      className={'input'}
                    >
                      <option disabled value={''}>Velg kategori ...</option>
                      {paramNames
                        // One line per category: the server merges repeats of the
                        // same category into a single OR-group anyway
                        .filter(p => p.key === criterion.paramKey
                          || !criteria.some(c => c.paramKey === p.key))
                        .map(paramName => (
                          <option value={paramName.key} key={paramName.key}>
                            {paramName.displayName}
                          </option>
                        ))}
                    </select>
                    <button
                      className={`${styles['icon-button']} ml1`}
                      onClick={() => removeCriterion(index)}
                      type="button"
                      aria-label="Fjern kategori"
                    ><i className="mdi mdi-minus"></i></button>
                  </div>
                  {criterion.paramKey !== '' && (
                    <ReactSelect
                      isMulti
                      placeholder="Velg verdier ..."
                      noOptionsMessage={() => "Ingen verdier"}
                      menuPortalTarget={document.body}
                      isLoading={isLoading}
                      options={options}
                      value={options.filter(option => criterion.values.includes(option.value))}
                      onChange={newValue => setCriterion(index, {
                        values: newValue.map(option => option.value),
                      })}
                      styles={{ menuPortal: base => ({
                        ...base,
                        zIndex: 9999999,
                      }) }}
                    />
                  )}
                </div>
              )
            })}

            {!appliesToAllTraffic && criteria.length < paramNames.length && (
              <button
                className={styles['add-criterion']}
                onClick={addCriterion}
                type="button"
              ><i className="mdi mdi-plus"></i> Legg til kategori</button>
            )}

            <div className={'row align-center'}>
              <span className={styles['scale-label']}>Skalering</span>
              <input
                className={`input ${styles['scale-input']}`}
                type="text"
                value={newScalingFactor}
                onChange={event => setNewScalingFactor(event.target.value)}
              />
              <button
                className={'ml1'}
                onClick={addModifier}
                type="button"
                disabled={!canAddModifier}
              >Legg til</button>
            </div>
          </div>

          {errorMessage !== undefined && (
            <div className={'validation-error'}>
              <i className="mdi mdi-alert-circle-outline"></i>
              <span className="ml2">{errorMessage}</span>
            </div>
          )}
        </div>

        <div className={`${styles.preview} column`}>
          <div className={'row space-between align-center'}>
            <span className={'form-label'}>Datagrunnlag</span>
            <span className={styles.counter}>
              {`${previewRows.length} rader, ${changedRowCount} endret`}
            </span>
          </div>

          <div className={`${styles['column-options']} row wrap`}>
            {table.getAllLeafColumns().map(column => (
              <label className={`${styles.option} row align-center`} key={column.id}>
                <input
                  type="checkbox"
                  checked={!hiddenColumnIds.has(column.id)}
                  onChange={() => toggleColumn(column.id)}
                />
                <span>{String(column.columnDef.header)}</span>
              </label>
            ))}
          </div>

          <div className={`${styles['table-container']} ${isLoading ? styles['is-loading'] : ''}`}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className={isNumericColumn(header.column.id) ? styles.numeric : ''}
                        aria-sort={ariaSort(header.column.getIsSorted())}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <i className={sortIndicatorClassName(header.column.getIsSorted())}></i>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {previewRows.length === 0 && (
                  <tr>
                    <td className={styles['table-message']} colSpan={Math.max(columns.length, 1)}>
                      {isLoading ? "Henter trafikk ..." : "Ingen trafikkdata for scenariet"}
                    </td>
                  </tr>
                )}
                {table.getRowModel().rows.map(row => (
                  <tr
                    className={`${row.original.isExcluded ? styles.excluded : ''} `
                      + `${row.original.isChanged && !row.original.isExcluded ? styles.changed : ''}`}
                    key={row.id}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={isNumericColumn(cell.column.id) ? styles.numeric : ''}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ModalBase>
  )
}
